import React, { useMemo } from 'react';
import type { CompsWidgetInstanceConfig, CompsData } from './types';
import { formatMultiple, DEFAULT_COLORS } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, KeyMetricBadge } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { BarChartSeries, ChartLegendSeries } from '../../../blocks/charts';

const SAMPLE_DATA: CompsData = {
  ticker: 'SAMPLE',
  comps_table: [
    { symbol: 'SAMPLE', pe_ratio: 37.1, ev_ebitda: 28.6, is_target: true },
    { symbol: 'PEER1', pe_ratio: 28.9, ev_ebitda: 21.4, is_target: false },
    { symbol: 'PEER2', pe_ratio: 21.9, ev_ebitda: 14.2, is_target: false },
    { symbol: 'PEER3', pe_ratio: 24.8, ev_ebitda: 15.6, is_target: false },
    { symbol: 'PEER4', pe_ratio: 32.8, ev_ebitda: 27.1, is_target: false },
  ],
};

interface Props {
  config: CompsWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const CompsWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<CompsData>(
    'comps',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;

  const chartData = useMemo(() => {
    const rows = d?.comps_table ?? [];
    return rows
      .filter((r) => typeof r.pe_ratio === 'number' || typeof r.ev_ebitda === 'number')
      .map((r) => ({ symbol: r.symbol, pe_ratio: r.pe_ratio, ev_ebitda: r.ev_ebitda }));
  }, [d]);

  // A single target with no peers isn't a comparison — don't draw a lone two-bar
  // chart. Keep the target's own multiples in the header, show an honest message.
  const peerCount = (d?.comps_table ?? []).filter(
    (r) => !r.is_target && (typeof r.pe_ratio === 'number' || typeof r.ev_ebitda === 'number'),
  ).length;
  const chartRows = peerCount === 0 ? [] : chartData;

  const series = useMemo<BarChartSeries[]>(() => [
    { yKey: 'pe_ratio', yName: 'P/E' },
    { yKey: 'ev_ebitda', yName: 'EV/EBITDA' },
  ], []);

  // Names-only legend — values vary per peer, so no single trailing readout.
  const legendSeries = useMemo<ChartLegendSeries[]>(() => series.map((s, i) => ({
    key: s.yKey,
    name: s.yName,
    color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  })), [series]);

  const spec: ChartSpec = {
    chartKind: 'bar',
    rows: chartRows,
    xKey: 'symbol',
    barSeries: series,
    showEndValueTags: false,
    yLabelFormatter: (params) => formatMultiple(Number(params.value)),
    xTitle: 'Peer',
    yTitles: { a: 'Multiple' },
    legendSeries: chartRows.length ? legendSeries : undefined,
    footnote: 'Source: FMP',
    emptyMessage: 'No comparable companies available',
  };

  const target = (d?.comps_table ?? []).find((r) => r.is_target);

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <span className="text-sm font-semibold text-neutral-800 mr-1">Comps</span>
      {target && (
        <>
          <KeyMetricBadge label={`${target.symbol} P/E`} value={formatMultiple(target.pe_ratio)} />
          <KeyMetricBadge label={`${target.symbol} EV/EBITDA`} value={formatMultiple(target.ev_ebitda)} />
        </>
      )}
    </div>
  );

  return (
    <ChartWidget loading={loading} error={error} refetch={refetch} isPreview={isPreview} spec={spec} header={header} />
  );
};

export default CompsWidgetRenderer;
